"use client";
// ────────── Bottom nav (mobile) + Top nav (desktop) — from Claude Design prototype ──────────
import React from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { VColor, VFont } from './tokens';
import { IconSearch, IconCompass, IconBell, IconSliders } from './icons';

const items = [
  { id: 'search', path: '/', label: 'Recherche', Icon: IconSearch },
  { id: 'discovery', path: '/discovery', label: 'Explorer', Icon: IconCompass },
  { id: 'alerts', path: '/alerts', label: 'Alertes', Icon: IconBell },
  { id: 'preferences', path: '/preferences', label: 'Préférences', Icon: IconSliders },
] as const;

export function BottomNav() {
  const pathname = usePathname();
  const router = useRouter();

  const activeId = pathname === '/discovery' ? 'discovery'
    : pathname === '/alerts' ? 'alerts'
    : pathname === '/preferences' ? 'preferences'
    : 'search';

  return (
    <nav style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 30,
      paddingBottom: 'max(env(safe-area-inset-bottom), 22px)',
      paddingTop: 8,
      background: 'rgba(255,253,250,0.92)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderTop: `1px solid ${VColor.line}`,
      display: 'flex', justifyContent: 'space-around',
    }}>
      {items.map(({ id, path, label, Icon }) => {
        const active = activeId === id;
        return (
          <button key={id} onClick={() => router.push(path)} style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0',
            color: active ? VColor.accent : VColor.muted,
            fontFamily: VFont.sans, fontSize: 11, fontWeight: active ? 600 : 500,
            minHeight: 48,
          }}>
            <span style={{
              position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 48, height: 28, borderRadius: 999,
              background: active ? VColor.accentSoft : 'transparent',
              transition: 'background 180ms ease',
            }}>
              <Icon size={20} sw={active ? 2 : 1.75} />
            </span>
            <span>{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

// ────────── Desktop topbar ──────────
export function DesktopTopbar() {
  const pathname = usePathname();
  const router = useRouter();

  const activeId = pathname === '/discovery' ? 'discovery'
    : pathname === '/alerts' ? 'alerts'
    : pathname === '/preferences' ? 'preferences'
    : 'search';

  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 30, height: 56,
      background: 'rgba(250,247,242,0.85)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      borderBottom: `1px solid ${VColor.line}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 28px',
    }}>
      {/* Wordmark */}
      <div style={{
        fontFamily: VFont.serif, fontSize: 22, fontWeight: 400, color: VColor.ink,
        letterSpacing: -0.5, cursor: 'pointer',
      }} onClick={() => router.push('/')}>
        <span style={{ color: VColor.accent }}>V</span>oyage
      </div>

      {/* Nav links */}
      <nav style={{ display: 'flex', gap: 28 }}>
        {items.map(({ id, path, label }) => (
          <button key={id} onClick={() => router.push(path)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0',
            fontFamily: VFont.mono, fontSize: 11, fontWeight: activeId === id ? 600 : 500,
            letterSpacing: 1.4, textTransform: 'uppercase' as const,
            color: activeId === id ? VColor.ink : VColor.muted,
            borderBottom: activeId === id ? `2px solid ${VColor.ink}` : '2px solid transparent',
          }}>
            {label}
          </button>
        ))}
      </nav>

      {/* Right side */}
      <div style={{
        fontFamily: VFont.mono, fontSize: 10, color: VColor.muted,
        letterSpacing: 0.8,
      }}>
        FR / EUR
      </div>
    </header>
  );
}

// ────────── Layout shell ──────────
export function LayoutShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      {/* Desktop topbar: hidden on mobile via CSS */}
      <div className="hidden lg:block">
        <DesktopTopbar />
      </div>

      {/* Main content area */}
      <main
        className="flex-1 mx-auto w-full max-w-[480px] md:max-w-[720px] lg:max-w-[1340px] lg:px-14 xl:px-0"
      >
        <div className="pb-[100px] lg:pb-0">
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <div className="block lg:hidden">
        <BottomNav />
      </div>
    </>
  );
}

export default BottomNav;
